/**
 * CropModal — Avatar crop dialog using react-easy-crop inside a Radix Dialog.
 *
 * Displays a square crop frame with zoom slider, "Save" and "Cancel" buttons.
 * Computes zoom constraints using shared utilities and clamps drag position
 * to ensure the image always fills the crop frame.
 *
 * Legacy avatar migration (Requirement 12.2): When initialCropParams is null
 * (user has profilePhotoS3Key but no stored crop params), react-easy-crop's
 * default behavior at zoom=1 with restrictPosition=true and aspect=1 produces
 * a centered square crop — functionally equivalent to computeDefaultCenterCrop.
 * The onCropComplete callback fires immediately on media load with the correct
 * centered crop area pixels, so the user can confirm without adjusting.
 *
 * Feature: avatar-crop
 * Requirements: 2.1, 2.2, 2.3, 2.4, 3.1, 3.3, 3.4, 3.5, 4.1, 4.3, 4.4, 12.2
 */

import { useCallback, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import type { Area, Point, MediaSize } from 'react-easy-crop';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  computeZoomConstraints,
  computeDefaultCenterCrop,
  type CropParams,
} from '@/lib/crop-constraints';

export interface CropModalProps {
  /** Presigned URL of the original image to crop */
  imageUrl: string;
  /** Previously saved crop params for re-crop (null for first crop) */
  initialCropParams: CropParams | null;
  /** Called when user confirms crop */
  onConfirm: (params: CropParams) => Promise<void>;
  /** Called when user cancels */
  onCancel: () => void;
  /** Whether the modal is open */
  open: boolean;
}

/**
 * Crop modal dialog for positioning a profile photo within a square frame.
 */
export function CropModal({
  imageUrl,
  initialCropParams,
  onConfirm,
  onCancel,
  open,
}: CropModalProps): JSX.Element {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [maxZoom, setMaxZoom] = useState(5);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);

  // Reset state when modal opens so stale croppedAreaPixels from a
  // previous session don't linger
  useEffect(() => {
    if (open) {
      setCroppedAreaPixels(null);
      setSaving(false);
      setImageDimensions(null);
    }
  }, [open]);

  /**
   * Called when react-easy-crop reports the image's natural dimensions.
   * We use this to compute zoom constraints and set initial crop if provided.
   *
   * Legacy migration note (Requirement 12.2): When initialCropParams is null,
   * react-easy-crop defaults to a centered view at zoom=1 which is equivalent
   * to computeDefaultCenterCrop. The onCropComplete callback fires on load
   * with the correct centered square crop area pixels.
   */
  const handleMediaLoaded = useCallback(
    (mediaSize: MediaSize) => {
      const { naturalWidth, naturalHeight } = mediaSize;
      setImageDimensions({ width: naturalWidth, height: naturalHeight });

      // react-easy-crop's zoom=1 means the image is scaled to fit inside the
      // crop area (contain). For a square crop (aspect=1), the shorter
      // dimension of the image matches the frame. Our minZoom should be 1
      // (the image already fills the frame at zoom=1 when using
      // objectFit="cover" or restrictPosition=true).
      //
      // With restrictPosition=true and aspect=1, react-easy-crop already
      // ensures the image fills the frame at zoom=1. So minZoom=1 and
      // maxZoom=5 aligns with our requirement of max = 5× min.
      const frameSize = Math.min(naturalWidth, naturalHeight);
      const constraints = computeZoomConstraints(
        naturalWidth,
        naturalHeight,
        frameSize,
      );

      // react-easy-crop normalizes zoom relative to its own "fit" calculation.
      // At zoom=1 with restrictPosition=true, the image fills the crop area.
      // Our constraints.minZoom represents the ratio needed for the shorter
      // side to fill the frame, which equals 1 in react-easy-crop's system.
      const normalizedMin = 1;
      const normalizedMax = constraints.maxZoom / constraints.minZoom;

      setMinZoom(normalizedMin);
      setMaxZoom(normalizedMax);
      setZoom(normalizedMin);
    },
    [],
  );

  const handleCropChange = useCallback((location: Point) => {
    setCrop(location);
  }, []);

  const handleZoomChange = useCallback((newZoom: number) => {
    setZoom(newZoom);
  }, []);

  const handleCropComplete = useCallback(
    (_croppedArea: Area, croppedPixels: Area) => {
      setCroppedAreaPixels(croppedPixels);
    },
    [],
  );

  const handleSave = useCallback(async () => {
    // Use croppedAreaPixels from react-easy-crop if available,
    // otherwise fall back to a default center crop based on image dimensions
    let params: CropParams;
    if (croppedAreaPixels) {
      params = {
        x: Math.round(croppedAreaPixels.x),
        y: Math.round(croppedAreaPixels.y),
        width: Math.round(croppedAreaPixels.width),
        height: Math.round(croppedAreaPixels.height),
      };
    } else if (imageDimensions) {
      params = computeDefaultCenterCrop(imageDimensions.width, imageDimensions.height);
    } else {
      // No image loaded yet — can't save
      return;
    }

    setSaving(true);
    try {
      await onConfirm(params);
    } finally {
      setSaving(false);
    }
  }, [croppedAreaPixels, imageDimensions, onConfirm]);

  const handleSliderChange = useCallback(
    (value: number[]) => {
      setZoom(value[0]);
    },
    [],
  );

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onCancel(); }}>
      <DialogContent
        showCloseButton={false}
        className="sm:max-w-md"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>Position your photo</DialogTitle>
        </DialogHeader>

        <div className="relative mx-auto aspect-square w-full max-w-[320px] overflow-hidden rounded-md bg-muted">
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="rect"
            showGrid={false}
            restrictPosition={true}
            minZoom={minZoom}
            maxZoom={maxZoom}
            onCropChange={handleCropChange}
            onZoomChange={handleZoomChange}
            onCropComplete={handleCropComplete}
            onMediaLoaded={handleMediaLoaded}
            initialCroppedAreaPixels={initialCropParams ?? undefined}
          />
        </div>

        <div className="flex items-center gap-3 px-2">
          <span className="text-eyebrow-sm text-muted-foreground shrink-0">Zoom</span>
          <Slider
            min={minZoom}
            max={maxZoom}
            step={0.01}
            value={[zoom]}
            onValueChange={handleSliderChange}
            aria-label="Zoom"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || (!croppedAreaPixels && !imageDimensions)}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
